import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Save, Users, Loader2, Info, CreditCard } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  createClient,
  updateClient,
  listClientStatusTypes,
  getClientCompanyAvailability,
  CLIENT_DUPLICATE_COMPANY_MESSAGE,
  type Client,
  type ClientStatusType,
} from "@/services/clientsService";
import { listProducts, type Product } from "@/services/productsService";
import { listPlans, type Plan } from "@/services/plansService";
import { getClientPlanSummary } from "@/utils/planDisplay";
import { cn } from "@/lib/utils";

/**
 * Normaliza o nome da empresa como no servidor (`trim` + minúsculas).
 *
 * @param value Texto do campo empresa
 *
 * @returns Chave comparável para saber se o valor mudou em relação ao registro carregado
 */
function normalizeCompanyKey(value: string) {
  return value.trim().toLowerCase();
}

interface ClientFormProps {
  onBack: () => void;
  onSave: () => void;
  editingClient: Client | null;
}

interface FormData {
  identifier: string;
  name: string;
  email: string;
  company: string;
  description: string;
  status_id: number;
  product_ids: string[];
  plan_assignment_type: "pending" | "catalog" | "enterprise";
  plan_id: string;
  monthly_call_limit: string;
  monthly_token_limit: string;
  excess_rate_per_1m_tokens: string;
}

export const ClientForm = ({
  onBack,
  onSave,
  editingClient,
}: ClientFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    identifier: "",
    name: "",
    email: "",
    company: "",
    description: "",
    status_id: 1,
    product_ids: [],
    plan_assignment_type: "pending",
    plan_id: "",
    monthly_call_limit: "",
    monthly_token_limit: "",
    excess_rate_per_1m_tokens: "",
  });
  const [statusTypes, setStatusTypes] = useState<ClientStatusType[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyFieldError, setCompanyFieldError] = useState<string | null>(
    null,
  );
  const [companyAvailabilityChecking, setCompanyAvailabilityChecking] =
    useState(false);
  const companyAvailabilityAbortRef = useRef<AbortController | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadFormData();
  }, [editingClient]);

  useEffect(() => {
    return () => {
      companyAvailabilityAbortRef.current?.abort();
    };
  }, []);

  const loadFormData = async () => {
    setLoading(true);
    try {
      const [statusTypesRes, productsRes, plansRes] = await Promise.all([
        listClientStatusTypes(),
        listProducts({ limit: 100 }),
        listPlans({ limit: 100 }),
      ]);

      if (statusTypesRes.success && statusTypesRes.data) {
        const payload = statusTypesRes.data as
          | ClientStatusType[]
          | { data?: ClientStatusType[] };
        setStatusTypes(Array.isArray(payload) ? payload : (payload.data ?? []));
      }
      if (productsRes.success && productsRes.data) {
        const activeProducts = productsRes.data.products.filter(
          (product) =>
            product.status_name === "Ativo" ||
            product.status_name === "active" ||
            product.status?.name === "Ativo" ||
            product.status?.name === "active",
        );
        setProducts(activeProducts);
      }
      if (plansRes.success && plansRes.data) {
        setPlans(plansRes.data.data || []);
      }

      if (editingClient) {
        const summary = getClientPlanSummary(editingClient);
        setFormData({
          identifier: editingClient.identifier,
          name: editingClient.name,
          email: editingClient.email,
          company: editingClient.company,
          description: editingClient.description || "",
          status_id: editingClient.status?.id || editingClient.status_id,
          product_ids: editingClient.products.map((p) => p.id),
          plan_assignment_type: summary.assignment_type,
          plan_id: editingClient.plan_id || "",
          monthly_call_limit:
            summary.assignment_type === "enterprise" &&
            summary.monthly_call_limit != null
              ? String(summary.monthly_call_limit)
              : "",
          monthly_token_limit:
            summary.assignment_type === "enterprise" &&
            summary.monthly_token_limit != null
              ? String(summary.monthly_token_limit)
              : "",
          excess_rate_per_1m_tokens:
            summary.assignment_type === "enterprise" &&
            summary.excess_rate_per_1m_tokens != null
              ? String(summary.excess_rate_per_1m_tokens)
              : "",
        });
      } else {
        setFormData({
          identifier: "",
          name: "",
          email: "",
          company: "",
          description: "",
          status_id: 1,
          product_ids: [],
          plan_assignment_type: "pending",
          plan_id: "",
          monthly_call_limit: "",
          monthly_token_limit: "",
          excess_rate_per_1m_tokens: "",
        });
      }
      setCompanyFieldError(null);
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyBlur = useCallback(async () => {
    const rawCompany = formData.company;
    if (!rawCompany.trim()) {
      setCompanyFieldError(null);
      return;
    }

    if (
      editingClient &&
      normalizeCompanyKey(rawCompany) ===
        normalizeCompanyKey(editingClient.company)
    ) {
      setCompanyFieldError(null);
      return;
    }

    companyAvailabilityAbortRef.current?.abort();
    const controller = new AbortController();
    companyAvailabilityAbortRef.current = controller;
    setCompanyAvailabilityChecking(true);

    try {
      const response = await getClientCompanyAvailability(rawCompany, {
        excludeClientId: editingClient?.id ?? null,
        signal: controller.signal,
      });

      if (!response.success || !("data" in response) || response.data == null) {
        setCompanyFieldError(null);
        return;
      }

      if (!response.data.available) {
        setCompanyFieldError(CLIENT_DUPLICATE_COMPANY_MESSAGE);
      } else {
        setCompanyFieldError(null);
      }
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      setCompanyFieldError(null);
    } finally {
      setCompanyAvailabilityChecking(false);
    }
  }, [formData.company, editingClient]);

  const handleSubmit = async () => {
    if (!formData.identifier.trim()) {
      toast({
        title: "Erro de Validação",
        description: "Identificador é obrigatório",
        variant: "destructive",
      });
      return;
    }

    const identifierRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!identifierRegex.test(formData.identifier)) {
      toast({
        title: "Erro de Validação",
        description:
          "Identificador deve conter apenas letras minúsculas, números e hífens, sem espaços",
        variant: "destructive",
      });
      return;
    }

    if (!formData.name.trim()) {
      toast({
        title: "Erro de Validação",
        description: "Nome do cliente é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (!formData.email.trim()) {
      toast({
        title: "Erro de Validação",
        description: "Email é obrigatório",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Erro de Validação",
        description: "Email inválido",
        variant: "destructive",
      });
      return;
    }

    if (!formData.company.trim()) {
      toast({
        title: "Erro de Validação",
        description: "Empresa é obrigatória",
        variant: "destructive",
      });
      return;
    }

    if (companyFieldError) {
      toast({
        title: "Erro de Validação",
        description: companyFieldError,
        variant: "destructive",
      });
      return;
    }

    try {
      const preSubmitAvailability = await getClientCompanyAvailability(
        formData.company,
        { excludeClientId: editingClient?.id ?? null },
      );

      if (
        preSubmitAvailability.success &&
        "data" in preSubmitAvailability &&
        preSubmitAvailability.data != null &&
        !preSubmitAvailability.data.available
      ) {
        setCompanyFieldError(CLIENT_DUPLICATE_COMPANY_MESSAGE);
        toast({
          title: "Erro de Validação",
          description: CLIENT_DUPLICATE_COMPANY_MESSAGE,
          variant: "destructive",
        });
        return;
      }
    } catch {
      /* rede ou cancelamento: segue para o servidor validar */
    }

    setSaving(true);
    try {
      let planPayload: {
        plan_assignment_type: FormData["plan_assignment_type"];
        plan_id?: string | null;
        monthly_call_limit?: number | null;
        monthly_token_limit?: number | null;
        excess_rate_per_1m_tokens?: number | null;
      } = {
        plan_assignment_type: formData.plan_assignment_type,
      };

      if (formData.plan_assignment_type === "catalog") {
        if (!formData.plan_id) {
          toast({
            title: "Erro de Validação",
            description: "Selecione um plano do catálogo",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }
        planPayload = {
          plan_assignment_type: "catalog",
          plan_id: formData.plan_id,
          monthly_call_limit: null,
          monthly_token_limit: null,
          excess_rate_per_1m_tokens: null,
        };
      } else if (formData.plan_assignment_type === "enterprise") {
        const monthlyCallLimit = Number(formData.monthly_call_limit);
        const monthlyTokenLimit = Number(formData.monthly_token_limit);
        const excessRate = Number(formData.excess_rate_per_1m_tokens);

        if (
          !Number.isInteger(monthlyCallLimit) ||
          monthlyCallLimit < 0 ||
          !Number.isInteger(monthlyTokenLimit) ||
          monthlyTokenLimit < 0 ||
          Number.isNaN(excessRate) ||
          excessRate < 0
        ) {
          toast({
            title: "Erro de Validação",
            description:
              "Informe limites e taxa de excedente válidos (>= 0) para o plano Enterprise",
            variant: "destructive",
          });
          setSaving(false);
          return;
        }

        planPayload = {
          plan_assignment_type: "enterprise",
          plan_id: null,
          monthly_call_limit: monthlyCallLimit,
          monthly_token_limit: monthlyTokenLimit,
          excess_rate_per_1m_tokens: excessRate,
        };
      } else {
        planPayload = {
          plan_assignment_type: "pending",
          plan_id: null,
          monthly_call_limit: null,
          monthly_token_limit: null,
          excess_rate_per_1m_tokens: null,
        };
      }

      if (editingClient) {
        const updateData = {
          identifier: formData.identifier,
          name: formData.name,
          email: formData.email,
          company: formData.company,
          description: formData.description,
          status_id: formData.status_id,
          product_ids: formData.product_ids,
          ...planPayload,
        };

        const response = await updateClient(editingClient.id, updateData);

        if (response.success && response.data) {
          toast({
            title: "Sucesso",
            description: "Cliente atualizado com sucesso",
          });
          onSave();
        } else {
          throw new Error(
            response.error?.message || "Erro ao atualizar cliente",
          );
        }
      } else {
        const response = await createClient({
          identifier: formData.identifier,
          name: formData.name,
          email: formData.email,
          company: formData.company,
          description: formData.description,
          status_id: formData.status_id,
          product_ids: formData.product_ids,
          ...planPayload,
        });

        if (response.success && response.data) {
          toast({
            title: "Sucesso",
            description: "Cliente criado com sucesso. Um e-mail de boas-vindas foi enviado para o usuário associado verificar o endereço e criar a senha.",
          });
          onSave();
        } else {
          throw new Error(response.error?.message || "Erro ao criar cliente");
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleProduct = (productId: string) => {
    setFormData((prev) => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter((id) => id !== productId)
        : [...prev.product_ids, productId],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Users}
        title={editingClient ? "Editar Cliente" : "Novo Cliente"}
        description={
          editingClient
            ? "Atualize as informações do cliente"
            : "Cadastre um novo cliente no sistema"
        }
        onBack={onBack}
        backDisabled={saving}
        actions={
          <Button
            onClick={handleSubmit}
            disabled={saving || !!companyFieldError}
            className="bg-gradient-primary text-white hover:opacity-90"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Salvar Cliente
              </>
            )}
          </Button>
        }
      />

      <main className="container mx-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
              <CardDescription>
                Configure os dados principais do cliente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="identifier">Identificador *</Label>
                  <div className="group relative">
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    <div className="absolute left-0 top-6 z-50 hidden group-hover:block w-72 p-3 bg-popover text-popover-foreground text-sm rounded-md border shadow-md">
                      <p className="font-medium mb-1">
                        Regras do Identificador:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Apenas letras minúsculas (a-z)</li>
                        <li>Números (0-9)</li>
                        <li>Hífens (-) para separação</li>
                        <li>Sem espaços ou caracteres especiais</li>
                        <li>Deve ser único no sistema</li>
                        <li>Exemplo: tech-solutions ou empresa-01</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <Input
                  id="identifier"
                  value={formData.identifier}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      identifier: e.target.value.toLowerCase(),
                    })
                  }
                  placeholder="Ex: tech-solutions"
                  disabled={saving}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Nome Completo *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Ex: João Silva"
                    disabled={saving}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="Ex: joao.silva@empresa.com"
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="company">Empresa *</Label>
                    {companyAvailabilityChecking ? (
                      <span
                        className="inline-flex"
                        aria-label="Verificando disponibilidade do nome da empresa"
                      >
                        <Loader2
                          className="h-4 w-4 animate-spin text-muted-foreground"
                          aria-hidden
                        />
                      </span>
                    ) : null}
                  </div>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => {
                      setFormData({ ...formData, company: e.target.value });
                      setCompanyFieldError(null);
                    }}
                    onBlur={handleCompanyBlur}
                    placeholder="Ex: Tech Solutions Ltda"
                    disabled={saving || companyAvailabilityChecking}
                    aria-invalid={!!companyFieldError}
                    aria-describedby={
                      companyFieldError ? "company-error" : undefined
                    }
                    aria-busy={companyAvailabilityChecking}
                    className={cn(
                      companyFieldError &&
                        "border-destructive focus-visible:ring-destructive",
                    )}
                  />
                  {companyFieldError ? (
                    <p
                      id="company-error"
                      className="text-sm text-destructive"
                      role="alert"
                    >
                      {companyFieldError}
                    </p>
                  ) : null}
                </div>

                <div className="grid gap-2">
                  <Label>Status *</Label>
                  <Select
                    value={formData.status_id.toString()}
                    onValueChange={(value) =>
                      setFormData({ ...formData, status_id: parseInt(value) })
                    }
                    disabled={saving}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusTypes.map((status) => (
                        <SelectItem
                          key={status.id}
                          value={status.id.toString()}
                        >
                          {status.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Informações adicionais sobre o cliente"
                  rows={4}
                  disabled={saving}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Plano Comercial
              </CardTitle>
              <CardDescription>
                Atribua um plano do catálogo ou configure limites Enterprise
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label>Tipo de atribuição</Label>
                <Select
                  value={formData.plan_assignment_type}
                  onValueChange={(value: FormData["plan_assignment_type"]) =>
                    setFormData({
                      ...formData,
                      plan_assignment_type: value,
                      ...(value !== "catalog" ? { plan_id: "" } : {}),
                      ...(value !== "enterprise"
                        ? {
                            monthly_call_limit: "",
                            monthly_token_limit: "",
                            excess_rate_per_1m_tokens: "",
                          }
                        : {}),
                    })
                  }
                  disabled={saving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo de plano" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Configuração pendente</SelectItem>
                    <SelectItem value="catalog">Plano do catálogo</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.plan_assignment_type === "catalog" && (
                <div className="grid gap-2">
                  <Label>Plano do catálogo</Label>
                  <Select
                    value={formData.plan_id}
                    onValueChange={(value) =>
                      setFormData({ ...formData, plan_id: value })
                    }
                    disabled={saving}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um plano" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.plan_assignment_type === "enterprise" && (
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="grid gap-2">
                    <Label htmlFor="enterprise_monthly_call_limit">
                      Limite mensal de acionamentos
                    </Label>
                    <Input
                      id="enterprise_monthly_call_limit"
                      type="number"
                      min={0}
                      step={1}
                      value={formData.monthly_call_limit}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          monthly_call_limit: e.target.value,
                        })
                      }
                      disabled={saving}
                      placeholder="Ex: 50000"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="enterprise_monthly_token_limit">
                      Limite mensal de tokens
                    </Label>
                    <Input
                      id="enterprise_monthly_token_limit"
                      type="number"
                      min={0}
                      step={1}
                      value={formData.monthly_token_limit}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          monthly_token_limit: e.target.value,
                        })
                      }
                      disabled={saving}
                      placeholder="Ex: 100000000"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="enterprise_excess_rate">
                      Taxa de excedente (R$ / 1M tokens)
                    </Label>
                    <Input
                      id="enterprise_excess_rate"
                      type="number"
                      min={0}
                      step="0.01"
                      value={formData.excess_rate_per_1m_tokens}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          excess_rate_per_1m_tokens: e.target.value,
                        })
                      }
                      disabled={saving}
                      placeholder="Ex: 40.00"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Especialistas Disponíveis</CardTitle>
              <CardDescription>
                Selecione os especialistas que este cliente terá acesso
              </CardDescription>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">
                    Nenhum especialista disponível
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-all ${
                          formData.product_ids.includes(product.id)
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => toggleProduct(product.id)}
                      >
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            checked={formData.product_ids.includes(product.id)}
                            onChange={() => toggleProduct(product.id)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={saving}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                          />
                          <div className="flex-1">
                            <div className="font-medium">{product.name}</div>
                            {product.description && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {product.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {formData.product_ids.length > 0 && (
                    <div className="mt-4 flex items-center space-x-2">
                      <Badge variant="secondary">
                        {formData.product_ids.length} especialista
                        {formData.product_ids.length !== 1 ? "s" : ""}{" "}
                        selecionado
                        {formData.product_ids.length !== 1 ? "s" : ""}
                      </Badge>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};
